import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CreditCard, Loader2, CheckCircle, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentPage() {
  const { invoiceId } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/pay/${invoiceId}`)
      .then(r => setInvoice(r.data))
      .catch(() => setError("Invoice not found"))
      .finally(() => setLoading(false));
  }, [invoiceId]);

  const handlePay = async () => {
    setPaying(true);
    setError("");
    try {
      const origin = window.location.origin;
      const { data } = await axios.post(`${API}/pay/${invoiceId}/checkout`, { origin_url: origin });
      if (data.url) window.location.href = data.url;
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to create payment session");
      setPaying(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><Loader2 className="w-6 h-6 text-[#00F0FF] animate-spin" /></div>;

  if (error && !invoice) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-white text-lg">{error}</p>
        </div>
      </div>
    );
  }

  const isPaid = invoice?.status === "paid";

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="font-outfit font-bold text-2xl text-white">WeDoWeDo OÜ</h1>
          <p className="text-xs text-neutral-500 font-mono tracking-[0.15em] mt-1">INVOICE PAYMENT</p>
        </div>

        <div className="bg-[#0a0a0a] border border-white/10 rounded-sm overflow-hidden">
          <div className="p-6 border-b border-white/5">
            <div className="flex justify-between items-start mb-4">
              <div>
                <span className="font-mono text-[10px] text-neutral-600 block">INVOICE</span>
                <span className="text-sm text-white font-mono">{invoice?.id?.slice(0, 8).toUpperCase()}</span>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-sm font-mono ${isPaid ? "bg-green-500/10 text-green-400" : "bg-blue-500/10 text-blue-400"}`}>
                {invoice?.status}
              </span>
            </div>
            {invoice?.customer?.company_name && (
              <div className="mb-3">
                <span className="font-mono text-[10px] text-neutral-600 block">BILL TO</span>
                <span className="text-sm text-white">{invoice.customer.company_name}</span>
                {invoice.customer.org_number && <span className="text-xs text-neutral-500 block">Org: {invoice.customer.org_number}</span>}
              </div>
            )}
            {invoice?.due_date && (
              <div className="mb-3">
                <span className="font-mono text-[10px] text-neutral-600 block">DUE DATE</span>
                <span className="text-sm text-neutral-300">{invoice.due_date}</span>
              </div>
            )}
            {invoice?.created_at && (
              <div>
                <span className="font-mono text-[10px] text-neutral-600 block">INVOICE DATE</span>
                <span className="text-sm text-neutral-300">{invoice.created_at?.slice(0, 10)}</span>
              </div>
            )}
          </div>

          <div className="p-6 border-b border-white/5">
            {invoice?.items?.map((item, i) => (
              <div key={i} className="flex justify-between py-2 border-b border-white/5 last:border-0">
                <div>
                  <span className="text-sm text-white">{item.description}</span>
                  <span className="text-xs text-neutral-500 block">Qty: {item.quantity}</span>
                </div>
                <span className="text-sm text-white font-mono">{(item.quantity * item.unit_price).toLocaleString()} {invoice.currency}</span>
              </div>
            ))}
          </div>

          <div className="p-6 space-y-2 border-b border-white/5">
            <div className="flex justify-between text-sm">
              <span className="text-neutral-500">Subtotal</span>
              <span className="text-neutral-300 font-mono">{(invoice?.subtotal || invoice?.net_amount || invoice?.total_amount)?.toLocaleString()} {invoice?.currency}</span>
            </div>
            {invoice?.reverse_charge && (
              <div className="flex justify-between text-sm">
                <span className="text-neutral-500">VAT 0% (Reverse Charge)</span>
                <span className="text-neutral-500 font-mono">0.00 {invoice?.currency}</span>
              </div>
            )}
            {!invoice?.reverse_charge && invoice?.vat_rate > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-neutral-500">VAT ({invoice.vat_rate}%)</span>
                <span className="text-neutral-300 font-mono">{invoice.vat_amount?.toLocaleString()} {invoice?.currency}</span>
              </div>
            )}
            {!invoice?.reverse_charge && !invoice?.vat_rate && invoice?.vat_type === "no_vat" && (
              <div className="flex justify-between text-sm">
                <span className="text-neutral-500">VAT</span>
                <span className="text-neutral-500 font-mono">N/A</span>
              </div>
            )}
            <div className="flex justify-between items-center pt-2 border-t border-white/5">
              <span className="text-white text-sm font-medium">Total</span>
              <span className="font-outfit font-bold text-2xl text-white">{invoice?.total_amount?.toLocaleString()} {invoice?.currency}</span>
            </div>
            {invoice?.reverse_charge && (
              <p className="text-[10px] text-[#00F0FF] font-mono mt-1">Reverse Charge — VAT liability transfers to buyer (EU Dir. 2006/112/EC, Art. 196)</p>
            )}
          </div>

          <div className="p-6">
            {isPaid ? (
              <div data-testid="payment-paid" className="flex items-center justify-center gap-2 py-3 bg-green-500/10 border border-green-500/20 rounded-sm">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="text-green-400 font-medium">Paid</span>
              </div>
            ) : (
              <>
                {error && <p className="text-red-400 text-sm mb-3">{error}</p>}
                <button
                  data-testid="pay-with-card-btn"
                  onClick={handlePay}
                  disabled={paying}
                  className="w-full bg-[#00F0FF] text-black font-medium py-3.5 rounded-sm hover:shadow-[0_0_25px_rgba(0,240,255,0.5)] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                  {paying ? "Redirecting to Stripe..." : `Pay ${invoice?.total_amount?.toLocaleString()} ${invoice?.currency}`}
                </button>
                <p className="text-xs text-neutral-600 text-center mt-3">Secure payment powered by Stripe</p>
              </>
            )}
          </div>
        </div>

        <div className="text-center mt-6">
          <p className="text-xs text-neutral-700 font-mono">WeDoWeDo OÜ &middot; Reg: 17455779 &middot; Tallinn, Estonia</p>
        </div>
      </div>
    </div>
  );
}
